/**
 * Gráficos de barras — hechos a mano, sin librería.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO USAMOS UNA LIBRERÍA DE GRÁFICOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las librerías de gráficos pesan entre 50 y 300 KB. Eso son varios segundos de
 * espera en el celular de una bodega con señal de 3G, y un archivo más que
 * puede fallar justo cuando no hay internet.
 *
 * Y para lo que necesita Softfi —barras verticales con su etiqueta— no hacen
 * falta: son `div`s con una altura en porcentaje. Se adaptan solas al ancho de
 * la pantalla, respetan el modo oscuro sin configurar nada y pesan cero.
 *
 * Si algún día hiciera falta un gráfico de verdad (líneas superpuestas,
 * dispersión), ahí sí se trae una librería. Hoy sería pagar mucho por nada.
 */

import type { ReactNode } from "react";
import {
  GloboDeGrafica,
  LineaDestacada,
  agruparPorPeriodo,
  etiquetaDelGrupo,
  hoyIso,
  posicionDelGlobo,
  tituloDelGrupo,
} from "@/components/Graficas";
import { cx } from "@/components/ui";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";

export type BarraDato = {
  /** Lo que va debajo de la barra: "lun", "14h", "Efectivo". */
  etiqueta: string;
  valor: string;
  /** Texto adicional para el globo de ayuda: "3 ventas". */
  detalle?: string;
  /** Marca esta barra como la de hoy / la actual. */
  destacada?: boolean;
  /** El título del globo: «jueves, 24 sept.». Sin él, la etiqueta. */
  titulo?: string;
  /**
   * La fecha de la barra (ISO). Si TODAS la traen y son más de 45, la
   * gráfica las junta por semana o por mes (2026-10-03).
   */
  fecha?: string;
  /** Lo que se cuenta en esa barra (ventas), para sumarlo al juntarlas. */
  cuenta?: number;
};

/**
 * Barras verticales.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ CON EL ESTILO DE «TODAS LAS SUCURSALES» (2026-10-03)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño vio las gráficas de «Todas las sucursales» y pidió que las de cada
 * negocio fueran iguales. Lo que cambió:
 *
 *   - **Colores planos.** La barra más alta (o la de hoy) va en el color del
 *     acento; las demás, en el mismo color más suave. El degradado y el brillo
 *     de antes se veían como adorno.
 *   - **El globo dice de qué se trata**: el día completo («jueves, 24 sept.»),
 *     el importe y el detalle, como en el de las sucursales.
 *   - **La conclusión debajo** (`pico`): «El día de mayor venta: … · $1.206».
 *   - **Períodos largos agrupados** por semana o por mes cuando las barras son
 *     días (`fecha`): noventa columnas de dos píxeles no se leen.
 *
 * Lo que se mantuvo, porque era correcto:
 *
 *   - **Las barras en cero se dibujan igual**, con una línea de 2 px. Una barra
 *     invisible haría creer que ese día no existe, cuando lo que dice es "ese
 *     día no vendiste" — que es información, y de la importante.
 *   - **La barra más alta llega al 100%** y las demás son proporcionales a ella.
 *     Sin eje numérico: en un panel se compara la forma, no se leen valores.
 *   - **Solo se etiquetan algunas** cuando hay muchas barras, o los textos se
 *     amontonan y no se lee ninguna.
 */
export function BarrasVerticales({
  datos: originales,
  moneda,
  alto = 140,
  className,
  pico,
  nombreDelValor = "Total",
  contar,
}: {
  datos: BarraDato[];
  moneda: FormatoMoneda;
  alto?: number;
  className?: string;
  /** La frase de la conclusión: «El día de mayor venta». Sin ella, no hay línea. */
  pico?: string;
  /** Cómo se llama el importe en el globo: «Vendido», «Gastos». */
  nombreDelValor?: string;
  /** Al juntar días: cómo se dice lo contado («venta», «ventas»). */
  contar?: [string, string];
}) {
  if (originales.length === 0) return null;

  // ── Los días juntos por semana o por mes, si hacen falta ─────────────────
  const conFecha = originales.every((d) => d.fecha);
  const agrupado = conFecha ? agruparPorPeriodo(originales.map((d) => d.fecha!)) : null;
  const hoy = hoyIso();
  const datos: BarraDato[] =
    agrupado && agrupado.modo !== "dia"
      ? agrupado.grupos.map((g) => {
          const valor = g.indices.reduce((s, i) => s + toNumber(originales[i]!.valor), 0);
          const cuenta = g.indices.reduce((s, i) => s + (originales[i]!.cuenta ?? 0), 0);
          return {
            etiqueta: etiquetaDelGrupo(agrupado.modo, g, agrupado.grupos.length),
            titulo: tituloDelGrupo(agrupado.modo, g),
            valor: String(valor),
            detalle: contar ? `${cuenta} ${cuenta === 1 ? contar[0] : contar[1]}` : undefined,
            destacada: g.desde <= hoy && hoy <= g.hasta,
          };
        })
      : originales;

  const valores = datos.map((d) => toNumber(d.valor));
  const maximo = Math.max(...valores, 0);

  // Cuál es el pico. Se marca con color pleno mientras las demás van en un tono
  // más suave: es la lectura que se hace de un vistazo —"¿cuándo vendí más?"—
  // y con todas las barras del mismo color hay que compararlas una por una.
  const indicePico = maximo > 0 ? valores.indexOf(maximo) : -1;
  const barraPico = indicePico >= 0 ? datos[indicePico] : undefined;

  // Con más de 12 barras se etiqueta una de cada N para que no se solapen.
  const salto = datos.length > 24 ? 4 : datos.length > 12 ? 2 : 1;

  return (
    <div className={cx("w-full", className)}>
      {agrupado && agrupado.modo !== "dia" && (
        <p className="mb-2 text-xs text-muted">
          {agrupado.modo === "semana"
            ? "Cada columna corresponde a una semana (de lunes a domingo)."
            : "Cada columna corresponde a un mes."}
        </p>
      )}
      <div className={cx("flex items-end", datos.length > 20 ? "gap-[3px]" : "gap-1.5")} style={{ height: alto }}>
        {datos.map((d, i) => {
          const valor = valores[i] ?? 0;
          // `maximo` en 0 significa que no se vendió nada en todo el rango:
          // todas las barras quedan en su altura mínima, que es lo correcto.
          const porcentaje = maximo > 0 ? (valor / maximo) * 100 : 0;

          return (
            <div key={`${d.etiqueta}-${i}`} className="group relative flex h-full min-w-0 flex-1 items-end">
              <div
                className={cx(
                  "barra-crece w-full rounded-t-[4px] transition-[filter,background-color] duration-150 group-hover:brightness-110",
                  d.destacada || i === indicePico
                    ? "bg-accent"
                    : valor > 0
                      ? "bg-accent/35 group-hover:bg-accent/60"
                      : "bg-line-strong",
                )}
                style={{
                  // El mínimo de 2 px es lo que hace visible un día sin ventas.
                  height: `max(2px, ${porcentaje}%)`,
                  // Crecen una detrás de otra, de izquierda a derecha, como se
                  // lee. Se corta a los 250 ms: con treinta barras, esperar por
                  // la última sería esperar por un adorno.
                  animationDelay: `${Math.min(i * 22, 250)}ms`,
                }}
              />
              <GloboDeGrafica
                titulo={d.titulo ?? d.etiqueta}
                filas={[{ nombre: nombreDelValor, valor: formatMoney(d.valor, moneda) }]}
                nota={d.detalle}
                posicion={posicionDelGlobo(i, datos.length)}
              />
            </div>
          );
        })}
      </div>

      {/*
        Etiquetas debajo.

        ⚠️ NO se recortan, y ese es el arreglo.

        Con `truncate`, una columna de 27 px convertía «12am» en «12a…» — o sea,
        que el eje del gráfico de horas no se podía leer en ningún celular.
        Como las de al lado van en blanco (se etiqueta una de cada `salto`),
        aquí sobra sitio: se deja que el texto se salga hacia los huecos
        vecinos, que para eso están.
      */}
      <div className={cx("mt-1.5 flex", datos.length > 20 ? "gap-[3px]" : "gap-1.5")}>
        {datos.map((d, i) => {
          const seEtiqueta = i % salto === 0 || d.destacada;
          return (
            <div
              key={`e-${d.etiqueta}-${i}`}
              className={cx(
                "min-w-0 flex-1 whitespace-nowrap text-center text-[10px] leading-none",
                d.destacada ? "font-semibold text-accent" : "text-faint",
                // Las de las puntas se alinean a su lado: centradas se saldrían
                // de la tarjeta, que es el mismo problema que el globo.
                seEtiqueta && i === 0 && "text-left",
                seEtiqueta && i === datos.length - 1 && "text-right",
              )}
            >
              {seEtiqueta ? d.etiqueta : " "}
            </div>
          );
        })}
      </div>

      {pico && barraPico && maximo > 0 && (
        <LineaDestacada
          texto={pico}
          resaltado={barraPico.titulo ?? barraPico.etiqueta}
          valor={formatMoney(barraPico.valor, moneda)}
        />
      )}
    </div>
  );
}

/**
 * Barras horizontales con su nombre y su importe: el desglose por método de
 * pago, por moneda o por categoría.
 *
 * Se usa horizontal y no vertical porque las etiquetas son palabras ("Pago
 * Móvil", "Binance / USDT") y en vertical habría que girarlas o recortarlas.
 *
 * ⚠️ Con el estilo de las listas de «Todas las sucursales» (2026-10-03): una
 * fila por cosa, con su puesto, separadas por una raya, y la barra debajo. La
 * primera en el color pleno; las demás, más suaves.
 */
export function BarrasHorizontales({
  datos,
  moneda,
  className,
  numerar = true,
}: {
  datos: { nombre: string; total: string; detalle?: string }[];
  moneda: FormatoMoneda;
  className?: string;
  /** El puesto a la izquierda: «1», «2»… */
  numerar?: boolean;
}) {
  if (datos.length === 0) {
    return <p className="py-6 text-center text-sm text-muted">Sin datos para mostrar en este período.</p>;
  }

  const total = datos.reduce((s, d) => s + Math.abs(toNumber(d.total)), 0);
  const maximo = Math.max(...datos.map((d) => Math.abs(toNumber(d.total))), 0);

  return (
    <ol className={className}>
      {datos.map((d, i) => {
        const valor = toNumber(d.total);
        const ancho = maximo > 0 ? (Math.abs(valor) / maximo) * 100 : 0;
        // El porcentaje sobre el total es lo que responde de verdad la pregunta
        // "¿cómo me pagan?" — más que el importe suelto.
        const parte = total > 0 ? (Math.abs(valor) / total) * 100 : 0;
        const sangria = numerar ? "ml-7" : undefined;

        return (
          <li key={d.nombre} className="border-t border-line py-3 first:border-t-0 first:pt-1">
            <div className="flex items-baseline justify-between gap-4">
              <p className="flex min-w-0 items-baseline gap-2.5">
                {numerar && (
                  <span className="tabular w-5 shrink-0 text-right text-xs font-semibold text-faint">{i + 1}</span>
                )}
                <span className="truncate text-sm font-medium text-ink" title={d.nombre}>
                  {d.nombre}
                </span>
              </p>
              <p className="shrink-0 text-right">
                <span className="tabular text-sm font-semibold text-ink">{formatMoney(d.total, moneda)}</span>
                <span className="tabular ml-2 text-xs text-muted">
                  {parte.toLocaleString("es-VE", { maximumFractionDigits: 0 })} %
                </span>
              </p>
            </div>
            <div className={cx("mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2", sangria)}>
              <div
                className={cx("h-full rounded-full transition-[width] duration-500", i === 0 ? "bg-accent" : "bg-accent/55")}
                style={{ width: `${ancho}%` }}
              />
            </div>
            {d.detalle && <p className={cx("mt-1 text-xs text-muted", sangria)}>{d.detalle}</p>}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Facturación y utilidad, mes a mes, EN DOS BARRAS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ DOS Y NO UNA ENCAJADA DENTRO DE OTRA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La primera versión metía la utilidad dentro de la barra de facturación. Leía
 * bien la proporción de un mes —«de esto, esto me quedé»— y leía mal lo que más
 * se mira aquí, que es la otra comparación: **cómo va la utilidad de un mes
 * contra la del mes anterior**. Encajada, esa línea no existe; hay que mirar
 * doce trocitos a distinta altura y adivinar.
 *
 * Con dos barras vecinas cada serie tiene su propio perfil de arriba abajo y se
 * siguen las dos de un vistazo. Y el mes en pérdida cuelga por debajo del cero,
 * que es la única forma de que un mes malo se vea como un mes malo y no como un
 * mes flojo.
 *
 * ⚠️ ESTA SÍ LLEVA EJE. Las demás barras de Softfi no lo llevan a propósito: en
 * un panel se compara la forma. Aquí no — es la pantalla donde se decide si el
 * negocio va bien, y «¿cuánto es esa barra?» es la pregunta, no un detalle.
 */
export function BarrasDeUtilidad({
  datos,
  moneda,
  alto = 210,
  className,
}: {
  datos: {
    etiqueta: string;
    /** Para el globo: "mar 2026". */
    titulo: string;
    facturado: string;
    /** `null` para quien no puede ver costos: se pinta solo lo facturado. */
    utilidad: string | null;
    /** Un mes que todavía no ha llegado: se deja en blanco, no en cero. */
    futuro?: boolean;
    destacada?: boolean;
  }[];
  moneda: FormatoMoneda;
  alto?: number;
  className?: string;
}) {
  if (datos.length === 0) return null;

  const valores = datos.flatMap((d) => [
    toNumber(d.facturado),
    d.utilidad === null ? 0 : toNumber(d.utilidad),
  ]);
  const techo = Math.max(...valores, 0);
  const suelo = Math.min(...valores, 0);

  // La escala se redondea hacia arriba a una cifra "bonita" para que las líneas
  // del eje caigan en números que alguien diría en voz alta: 20.000, no 18.437.
  const tope = escalaBonita(techo);
  const fondo = suelo < 0 ? -escalaBonita(Math.abs(suelo)) : 0;
  const rango = tope - fondo || 1;

  // Dónde cae el cero dentro del alto del gráfico. Con meses en pérdida no está
  // abajo del todo: las barras negativas necesitan sitio por debajo.
  const cero = ((tope - 0) / rango) * 100;
  const hayCostos = datos.some((d) => d.utilidad !== null);

  const lineas = [tope, fondo < 0 ? 0 : tope / 2, fondo].filter(
    (v, i, a) => a.indexOf(v) === i,
  );

  return (
    <div className={cx("w-full", className)}>
      <div className="flex gap-2">
        {/* El eje. Va fuera del área de barras para que no las estreche. */}
        <div
          className="relative w-11 shrink-0 text-right text-[10px] leading-none text-faint"
          style={{ height: alto }}
          aria-hidden
        >
          {lineas.map((v) => (
            <span
              key={v}
              className="absolute right-0 -translate-y-1/2 tabular"
              style={{ top: `${((tope - v) / rango) * 100}%` }}
            >
              {corto(v)}
            </span>
          ))}
        </div>

        <div className="relative min-w-0 flex-1" style={{ height: alto }}>
          {/* Las guías horizontales, por detrás de las barras. */}
          {lineas.map((v) => (
            <div
              key={v}
              className={cx(
                "absolute inset-x-0 border-t",
                v === 0 && fondo < 0 ? "border-line-strong" : "border-line",
              )}
              style={{ top: `${((tope - v) / rango) * 100}%` }}
              aria-hidden
            />
          ))}

          <div className="absolute inset-0 flex items-stretch gap-[3px]">
            {datos.map((d, i) => (
              <div key={`${d.etiqueta}-${i}`} className="group relative min-w-0 flex-1">
                {/*
                  ⚠️ Un mes que no ha llegado se deja VACÍO, no en cero.

                  Una barra de altura mínima en octubre dice «en octubre no
                  vendiste»; y lo que pasa es que octubre no ha llegado. Son dos
                  cosas distintas y en una gráfica se ven iguales.
                */}
                {!d.futuro && (
                  <div className="absolute inset-0">
                    <Columna
                      valor={toNumber(d.facturado)}
                      rango={rango}
                      cero={cero}
                      retraso={i * 18}
                      solo={d.utilidad === null}
                      lado="izq"
                      clase="bg-accent"
                    />
                    {d.utilidad !== null && (
                      <Columna
                        valor={toNumber(d.utilidad)}
                        rango={rango}
                        cero={cero}
                        retraso={i * 18 + 60}
                        lado="der"
                        clase={toNumber(d.utilidad) < 0 ? "bg-danger" : "bg-success"}
                      />
                    )}
                  </div>
                )}

                {/* El mismo globo que el resto de gráficas (2026-10-03). */}
                <GloboDeGrafica
                  titulo={d.titulo}
                  filas={
                    d.futuro
                      ? []
                      : [
                          { nombre: "Facturado", valor: formatMoney(d.facturado, moneda), color: "var(--c-accent)" },
                          ...(d.utilidad !== null
                            ? [
                                {
                                  nombre: "Utilidad",
                                  valor: formatMoney(d.utilidad, moneda),
                                  color: toNumber(d.utilidad) < 0 ? "var(--c-danger)" : "var(--c-success)",
                                },
                              ]
                            : []),
                        ]
                  }
                  nota={d.futuro ? "Mes aún no transcurrido" : undefined}
                  posicion={posicionDelGlobo(i, datos.length)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-1.5 flex gap-[3px] pl-[52px]">
        {datos.map((d, i) => (
          <div
            key={`e-${d.etiqueta}-${i}`}
            className={cx(
              "min-w-0 flex-1 whitespace-nowrap text-center text-[10px] leading-none",
              d.futuro ? "text-faint/50" : d.destacada ? "font-semibold text-accent" : "text-faint",
            )}
          >
            {d.etiqueta}
          </div>
        ))}
      </div>

      {hayCostos && (
        /*
          La leyenda va debajo y en una línea. Sin ella, la barra oscura y la
          verde son dos colores; con ella son dos cosas.
        */
        <div className="mt-3 flex flex-wrap items-center gap-4 pl-[52px] text-xs text-muted">
          <Leyenda clase="bg-accent">Facturado</Leyenda>
          <Leyenda clase="bg-success">Utilidad</Leyenda>
          <Leyenda clase="bg-danger">Mes en pérdida</Leyenda>
        </div>
      )}
    </div>
  );
}

function Columna({
  valor,
  rango,
  cero,
  retraso,
  lado,
  solo,
  clase,
}: {
  valor: number;
  rango: number;
  /** Dónde cae la línea del cero, en % desde arriba. */
  cero: number;
  retraso: number;
  lado: "izq" | "der";
  /** Sin la serie de utilidad, la de facturación ocupa el ancho entero. */
  solo?: boolean;
  clase: string;
}) {
  const alto = (Math.abs(valor) / rango) * 100;
  const negativo = valor < 0;

  return (
    <div
      className={cx(
        "barra-crece absolute opacity-90 transition-[filter,opacity] duration-150 group-hover:opacity-100",
        negativo ? "rounded-b-[3px]" : "rounded-t-[3px]",
        clase,
      )}
      style={{
        // ⚠️ Las positivas se apoyan en la línea del cero y SUBEN; las negativas
        // arrancan en ella y BAJAN. Sin esto, un mes en pérdida se dibujaría
        // como una barra corta y se leería como un mes flojo, que es lo
        // contrario de lo que pasó.
        top: negativo ? `${cero}%` : `calc(${cero}% - max(2px, ${alto}%))`,
        height: `max(2px, ${alto}%)`,
        animationDelay: `${Math.min(retraso, 300)}ms`,
        width: solo ? "100%" : "calc(50% - 1px)",
        left: solo || lado === "izq" ? 0 : "calc(50% + 1px)",
      }}
    />
  );
}

function Leyenda({ clase, children }: { clase: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cx("h-2.5 w-2.5 rounded-[3px]", clase)} />
      {children}
    </span>
  );
}

/** 18.437 → 20.000. Para que las líneas del eje caigan en números redondos. */
function escalaBonita(v: number): number {
  if (v <= 0) return 0;
  const magnitud = Math.pow(10, Math.floor(Math.log10(v)));
  return Math.ceil(v / magnitud) * magnitud;
}

/** 20000 → "20K". El eje tiene 44 px: no cabe "20.000,00". */
function corto(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(Math.round(v));
}

/**
 * El anillo del capital: qué parte de lo que tienes es cada cosa.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ UN ANILLO AQUÍ, SI LOS GRÁFICOS DE TORTA CASI NUNCA SON BUENA IDEA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque la pregunta es literalmente de proporción y son tres trozos. «Tengo
 * 130.209» no dice nada útil; «todo está en mercancía» dice que si llega el
 * proveedor el lunes hay un problema, aunque el total se vea enorme.
 *
 * Es anillo y no círculo lleno para poder poner el total en el centro: el dato
 * y su reparto en el mismo sitio, sin mirar a otra tarjeta.
 *
 * ⚠️ Solo van los ACTIVOS. Lo que se debe no es un trozo de lo que se tiene —
 * meterlo pintaría la deuda como si fuera parte del patrimonio.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LOS TRES ARREGLOS DE LA PRIMERA VERSIÓN, QUE SE VIERON CON DATOS REALES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. **Un trozo en cero desaparecía de la leyenda.** En una bodega con todo
 *      en el estante, la fila que faltaba era «Cuentas y cajón: 0» — o sea, el
 *      dato más importante de la pantalla. Ahora los que están en cero salen
 *      igual, apagados: que una parte no tenga nada ES la información.
 *
 *   2. **Un trozo diminuto salía como «0.0 %» con su punto de color y sin
 *      trozo en el anillo.** 5,40 de 130.209 no se puede dibujar sin mentir
 *      sobre la escala. La proporción del anillo se deja exacta y la leyenda lo
 *      dice con palabras («menos del 0,1 %») y con su importe al lado, que es
 *      lo que de verdad quería saber quien miró.
 *
 *   3. **Con un solo trozo era un aro de un color, sin información.** Ahora
 *      lleva su pista de fondo y sus separaciones, y —lo que de verdad cambia
 *      algo— la pantalla dice en una frase lo que el aro está enseñando.
 */
export function AnilloDeCapital({
  partes,
  centro,
  pie,
  moneda,
  className,
}: {
  partes: { etiqueta: string; valor: string; color: string }[];
  centro: string;
  pie?: string;
  moneda: FormatoMoneda;
  className?: string;
}) {
  const total = partes.reduce((suma, p) => suma + Math.max(toNumber(p.valor), 0), 0);

  const R = 62;
  const GROSOR = 15;
  const C = 2 * Math.PI * R;

  let recorrido = 0;
  const trozos = partes.map((p) => {
    const valor = Math.max(toNumber(p.valor), 0);
    const fraccion = total > 0 ? valor / total : 0;
    const trozo = { ...p, valor, fraccion, desfase: recorrido };
    recorrido += fraccion;
    return trozo;
  });

  const visibles = trozos.filter((t) => t.fraccion > 0);
  // La separación entre trozos solo tiene sentido si hay más de uno; en un
  // anillo de un solo color abriría una muesca que parecería un fallo.
  const hueco = visibles.length > 1 ? 2.5 : 0;

  return (
    <div className={cx("flex flex-wrap items-center gap-x-6 gap-y-4", className)}>
      <div className="relative shrink-0">
        <svg viewBox="0 0 160 160" className="h-[148px] w-[148px] -rotate-90" role="img">
          {/* La pista de fondo: hace que se lea como un anillo aunque haya un
              solo trozo, y da el borde exterior cuando el total es cero. */}
          <circle
            cx="80"
            cy="80"
            r={R}
            fill="none"
            strokeWidth={GROSOR}
            className="stroke-line"
          />
          {visibles.map((t) => (
            <circle
              key={t.etiqueta}
              cx="80"
              cy="80"
              r={R}
              fill="none"
              stroke={t.color}
              strokeWidth={GROSOR}
              // Cada trozo es el mismo círculo con un hueco: se dibuja su
              // fracción y se deja el resto en blanco, desplazado para que
              // empiece donde acabó el anterior.
              strokeDasharray={`${Math.max(t.fraccion * C - hueco, 0.5)} ${C}`}
              strokeDashoffset={-t.desfase * C}
            >
              <title>{`${t.etiqueta}: ${formatMoney(t.valor.toString(), moneda)}`}</title>
            </circle>
          ))}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <span
            className={cx(
              "tabular font-semibold leading-tight text-ink",
              // ⚠️ El importe manda en el tamaño. «$1.240,00» y «$130.209,09»
              // no caben igual dentro de un agujero de 90 px, y el segundo se
              // salía por los lados del anillo.
              centro.length > 11 ? "text-[13px]" : centro.length > 8 ? "text-sm" : "text-lg",
            )}
          >
            {centro}
          </span>
          {pie && <span className="mt-0.5 text-[10px] text-muted">{pie}</span>}
        </div>
      </div>

      <ul className="min-w-[190px] flex-1 space-y-2.5">
        {trozos.map((t) => {
          const vacio = t.valor <= 0;
          return (
            <li key={t.etiqueta} className="flex items-start gap-2.5">
              <span
                className={cx("mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]", vacio && "opacity-30")}
                style={{ background: t.color }}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cx(
                    "truncate text-sm leading-tight",
                    vacio ? "text-faint" : "text-muted",
                  )}
                >
                  {t.etiqueta}
                </p>
                <p
                  className={cx(
                    "tabular text-[11px] leading-tight",
                    vacio ? "text-faint" : "text-muted/80",
                  )}
                >
                  {formatMoney(t.valor.toString(), moneda)}
                </p>
              </div>
              <span
                className={cx(
                  "tabular shrink-0 text-sm font-semibold",
                  vacio ? "text-faint" : "text-ink",
                )}
              >
                {porcentajeDelAnillo(t.fraccion)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * El porcentaje de un trozo, dicho de forma que no engañe.
 *
 * ⚠️ «0,0 %» es la trampa. Redondear 0,004 % a un decimal da «0,0 %», que se
 * lee como «nada» al lado de un importe de 5,40 que sí existe — y quien mira se
 * queda pensando si la pantalla está rota. Por debajo del 0,1 % se dice con
 * palabras, y el importe de al lado completa el dato.
 *
 * ⚠️ Y «100,0 %» tampoco: el decimal en un cien redondo solo hace ruido.
 */
function porcentajeDelAnillo(fraccion: number): string {
  const pct = fraccion * 100;
  if (pct <= 0) return "0 %";
  if (pct < 0.1) return "< 0,1 %";
  if (pct >= 99.95) return "100 %";
  return `${pct.toFixed(1).replace(".", ",")} %`;
}
